import React, { useEffect, useState } from 'react';
import { USD_TO_INR } from '../../utils/constants';
import AiVerdictChip from '../ai/AiVerdictChip';
import LiquidationRiskBanner from './LiquidationRiskBanner';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  Collapse,
  Checkbox,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  TablePagination
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatPrice } from '../../utils/formatters';
import { resolveDecimalsFor } from '../../utils/decimalPrecision';
import { CH_TYPE, chNumeric, chHairline } from '../../styles/appUi';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { tradingService } from '../../services/trading.service';
import { Trade, TradeStatus } from '../../types';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';
import { usePersistedState } from '../../hooks/usePersistedState';
import { AppColorTokens } from '../../styles/colorTokens';
import { useConfirm } from '../common/ConfirmDialog';
import { useTabs } from '../../contexts/TabsContext';
import OptionBasketsMonitor from '../options/OptionBasketsMonitor';

interface TradesTableProps {
  trades?: Trade[];
}

const getTradeRowBg = (trade: Trade, C: AppColorTokens) => {
  if (trade.status === 'LIMBO' as any) return C.warnMuted;
  const pnl = (trade as any).totalPnl ?? (trade as any).unrealizedPnl ?? (trade as any).realizedPnl ?? 0;
  if (trade.status === TradeStatus.DRAFT) return C.neutralMuted;
  if (pnl > 0) return C.rowProfit;
  if (pnl < 0) return C.rowLoss;
  return 'transparent';
};

const StatusChip: React.FC<{ status: string; C: AppColorTokens }> = ({ status, C }) => {
  const cfg: Record<string, { label: string; color: string; bg: string }> = {
    OPEN:    { label: 'OPEN',    color: C.bull,    bg: C.bullMuted },
    CLOSED:  { label: 'CLOSED', color: C.dim,     bg: C.dimMuted },
    DRAFT:   { label: 'DRAFT',  color: C.neutral, bg: C.neutralMuted },
    LIMBO:   { label: 'LIMBO',  color: C.warn,    bg: C.warnMuted },
    PARTIAL: { label: 'PARTIAL',color: C.warn,    bg: C.warnMuted },
  };
  const c = cfg[status] || { label: status, color: C.dim, bg: C.dimMuted };
  return (
    <Chip
      label={c.label}
      size="small"
      /* Outlined, not filled. These rows already carry a P&L tint, so a translucent chip
         fill on top of one compounded to 3.79:1. Dropping the fill puts the label straight
         on the row (5.4-9.3:1 for every status colour) and the border still reads as a
         badge. */
      sx={{ backgroundColor: 'transparent', color: c.color, fontWeight: 700,
        fontSize: '0.7rem', border: `1px solid ${c.color}59` }}
    />
  );
};

/** Clickable section header bar with a chevron + count chip — drives the two
 *  collapsible sections (Option baskets / Trades) on the Trades page. */
const SectionBar: React.FC<{ open: boolean; onToggle: () => void; title: string; chip?: React.ReactNode }> = ({ open, onToggle, title, chip }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none',
      py: 1, px: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider',
      bgcolor: 'action.hover', transition: 'background-color .15s',
      '&:hover': { bgcolor: 'action.selected' },
    }}
  >
    {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
    <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem' }}>{title}</Typography>
    {chip}
  </Box>
);

const TradesTable: React.FC<TradesTableProps> = ({ trades: propTrades }) => {
  const confirm = useConfirm();
  const { openChartTab } = useTabs();
  const { C, isDark } = useAppTheme();
  const [trades, setTrades] = useState<Trade[]>(propTrades || []);
  const [loading, setLoading] = useState(false);
  const [profitLockMult, setProfitLockMult] = useState<number>(5);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedTrades, setSelectedTrades] = useState<Set<number>>(new Set());

  // Filters — persisted so they survive navigating away and back (issue #2).
  const [statusFilter, setStatusFilter] = usePersistedState<string>('trades.statusFilter', 'all');
  // R69b — server-side pagination. Persisted page size so user choice survives
  // page navigation. Default 50 rows fits a typical screen without scrolling
  // the table off-page and pairs with the OPEN-default for fast loads.
  const [page, setPage] = useState<number>(0); // 0-indexed for MUI compat
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem('trades_page_size') || '', 10);
    return Number.isFinite(saved) && saved > 0 ? saved : 50;
  });
  const [totalRows, setTotalRows] = useState<number>(0);
  const [sideFilter, setSideFilter] = usePersistedState<string>('trades.sideFilter', 'all');
  const [symbolFilter, setSymbolFilter] = usePersistedState<string>('trades.symbolFilter', '');
  const [tagFilter, setTagFilter] = usePersistedState<string>('trades.tagFilter', '');
  const [sortBy, setSortBy] = usePersistedState<string>('trades.sortBy.v2', 'closedAt');
  // Collapsible sections (Option baskets on top, single-instrument trades below).
  const [basketsOpen, setBasketsOpen] = usePersistedState<boolean>('trades.basketsOpen', true);
  const [tradesOpen, setTradesOpen] = usePersistedState<boolean>('trades.tradesOpen', true);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Inline SL editing (existing pattern preserved)
  const [editingSLTradeId, setEditingSLTradeId] = useState<number | null>(null);
  const [editingSLValue, setEditingSLValue] = useState<string>('');

  // Inline Target editing
  const [editingTargetTradeId, setEditingTargetTradeId] = useState<number | null>(null);
  const [editingTargetValue, setEditingTargetValue] = useState<string>('');

  // Drawer state
  const [drawerTradeId, setDrawerTradeId] = useState<number | null>(null);
  const [drawerTrade, setDrawerTrade] = useState<any>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // LIMBO resolve dialog state
  const [limboDialogTradeId, setLimboDialogTradeId] = useState<number | null>(null);
  const [limboClosePrice, setLimboClosePrice] = useState<string>('');

  // Order price/qty force-edit state (backdoor for fixing null-price MARKET orders)
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
  const [editOrderPrice, setEditOrderPrice] = useState<string>('');
  const [editOrderQty, setEditOrderQty] = useState<string>('');

  // Phase B — tag editing state
  const [tagDialogTrade, setTagDialogTrade] = useState<Trade | null>(null);
  const [tagDraft, setTagDraft] = useState<string>('');
  const [tagSubmitting, setTagSubmitting] = useState(false);

  const openTagDialog = (trade: Trade) => {
    setTagDialogTrade(trade);
    setTagDraft((trade as any).tags?.join(', ') || '');
  };

  // Phase B — per-trade journal notes
  const [notesDialogTrade, setNotesDialogTrade] = useState<Trade | null>(null);
  const [notesDraft, setNotesDraft] = useState<string>('');
  const [notesSubmitting, setNotesSubmitting] = useState(false);
  const openNotesDialog = (trade: Trade) => {
    setNotesDialogTrade(trade);
    setNotesDraft((trade as any).notes || '');
  };
  const saveNotes = async () => {
    if (!notesDialogTrade) return;
    setNotesSubmitting(true);
    try {
      await api.put(`/trades/${notesDialogTrade.id}/notes`, { notes: notesDraft });
      setNotesDialogTrade(null);
      setNotesDraft('');
      fetchTrades();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to update note');
    } finally {
      setNotesSubmitting(false);
    }
  };
  const saveTagsForDialog = async () => {
    if (!tagDialogTrade) return;
    setTagSubmitting(true);
    try {
      const parsed = tagDraft.split(',').map(s => s.trim()).filter(Boolean).slice(0, 12);
      await api.put(`/trades/${tagDialogTrade.id}/tags`, { tags: parsed });
      setTagDialogTrade(null);
      setTagDraft('');
      fetchTrades();
      api.get<{ success: boolean; data: string[] }>('/trades/tags').then(r => setAvailableTags(r.data?.data || [])).catch(() => {});
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to update tags');
    } finally {
      setTagSubmitting(false);
    }
  };

  // Phase B — Replay button: open Charts at the trade's entry timestamp with the symbol.
  // Opens in a new in-app tab via TabsContext (PWA-friendly).
  const handleReplay = (trade: Trade) => {
    const symbol = (trade as any).instrument?.symbol || trade.symbol;
    const exchange = ((trade as any).instrument?.exchange || (trade as any).exchange || '').toUpperCase();
    if (!symbol) return;
    const extra: Record<string, string> = {};
    if ((trade as any).executedAt) extra.focusAt = new Date((trade as any).executedAt).toISOString();
    if ((trade as any).originIntervalMinutes) extra.interval = String((trade as any).originIntervalMinutes);
    // Apply the strategy that opened the trade (numeric id preferred — gives the
    // persisted scanner markers; else the strategy name → client overlay).
    const stratRef = (trade as any).strategyId ?? (trade as any).strategy?.name ?? (trade as any).strategyName;
    if (stratRef != null && String(stratRef).trim()) extra.strategy = String(stratRef).trim();
    extra.tradeId = String(trade.id);
    openChartTab(symbol, exchange, extra);
  };

  const toggleRow = (tradeId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(tradeId)) {
      newExpanded.delete(tradeId);
    } else {
      newExpanded.add(tradeId);
      // R69b — lazy-fetch orders the first time a row is expanded. The list
      // payload no longer carries them (saves ~18k rows for active accounts).
      const trade = trades.find(t => t.id === tradeId);
      if (trade && (!('orders' in trade) || !trade.orders)) {
        (async () => {
          try {
            const resp = await api.get(`/trades/${tradeId}`);
            const detail = (resp.data as any)?.data;
            if (detail?.orders) {
              setTrades(prev => prev.map(t => t.id === tradeId ? ({ ...t, orders: detail.orders } as any) : t));
            }
          } catch { /* expansion still works, just no orders list */ }
        })();
      }
    }
    setExpandedRows(newExpanded);
  };

  const toggleSelectTrade = (tradeId: number) => {
    const newSelected = new Set(selectedTrades);
    if (newSelected.has(tradeId)) {
      newSelected.delete(tradeId);
    } else {
      newSelected.add(tradeId);
    }
    setSelectedTrades(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedTrades.size === trades.length) {
      setSelectedTrades(new Set());
    } else {
      setSelectedTrades(new Set(trades.map(t => t.id)));
    }
  };

  useEffect(() => {
    api.get('/user/settings').then((r: any) => {
      if (r.data?.success) {
        setProfitLockMult((r.data.data as any)?.additionalSettings?.profitLockMultiplier || 5);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!propTrades) {
      fetchTrades();
    }
  }, [propTrades]);

  // Re-fetch when filters or pagination change. Filter changes reset to
  // page 0 so user doesn't end up on page 5 of a 1-page result set.
  useEffect(() => {
    if (!propTrades) setPage(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sideFilter, symbolFilter, tagFilter, sortBy]);
  useEffect(() => {
    if (!propTrades) fetchTrades();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sideFilter, symbolFilter, tagFilter, sortBy, page, pageSize]);

  // Available tags for the filter dropdown (refreshed when tags are edited).
  useEffect(() => {
    if (propTrades) return;
    api.get<{ success: boolean; data: string[] }>('/trades/tags').then(r => setAvailableTags(r.data?.data || [])).catch(() => {});
  }, [propTrades]);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { page: page + 1, pageSize };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sideFilter !== 'all') params.side = sideFilter;
      if (symbolFilter) params.symbol = symbolFilter;
      if (tagFilter) params.tag = tagFilter;
      if (sortBy === 'closedAt') { params.sortBy = 'closedAt'; params.sortDir = 'DESC'; }

      const response = await tradingService.getTrades(params);

      if (response.data.success && response.data.data) {
        setTrades(response.data.data.data);
        const t = (response.data.data as any).total;
        if (Number.isFinite(t)) setTotalRows(Number(t));
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch trades');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await tradingService.syncPositions();
      fetchTrades();
    } catch (error) {
      console.error('Failed to sync positions:', error);
      fetchTrades();
    }
  };

  const handleCloseTrade = async (tradeId: number) => {
    const isPaper = !!trades.find(t => t.id === tradeId)?.isSimulation;
    const ok = await confirm({
      title: isPaper ? 'Close PAPER trade?' : 'Close trade?',
      body: isPaper
        ? 'Simulated — it closes at the live price. No broker order is placed.'
        : 'A MARKET order will be sent to the broker to close this position. This cannot be undone.',
      confirmLabel: 'Close trade',
      destructive: true,
    });
    if (!ok) return;

    try {
      await tradingService.closeTrade(tradeId, { closeType: 'MARKET' });
      fetchTrades();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to close trade');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedTrades.size === 0) return;

    const count = selectedTrades.size;
    const ok = await confirm({
      title: `Delete ${count} trade${count > 1 ? 's' : ''}?`,
      body: 'The selected trade records will be permanently removed from the database. Broker orders are unaffected.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      const deletePromises = Array.from(selectedTrades).map(tradeId =>
        tradingService.deleteTrade(tradeId)
      );
      await Promise.all(deletePromises);

      setSelectedTrades(new Set());
      fetchTrades();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete trades');
    }
  };

  const handleSaveSL = async (tradeId: number) => {
    try {
      const sl = parseFloat(editingSLValue);
      if (isNaN(sl) || sl <= 0) return;
      await tradingService.setManualStopLoss(tradeId, sl);
      setEditingSLTradeId(null);
      fetchTrades();
    } catch (err: any) {
      console.error('Failed to set SL:', err);
    }
  };

  const handleSaveTarget = async (tradeId: number) => {
    try {
      const target = parseFloat(editingTargetValue);
      if (isNaN(target) || target <= 0) return;
      await tradingService.setTarget(tradeId, target);
      setEditingTargetTradeId(null);
      fetchTrades();
    } catch (err: any) {
      console.error('Failed to set target:', err);
    }
  };

  const handleCancelDraft = async (tradeId: number) => {
    const ok = await confirm({
      title: 'Cancel draft order?',
      body: 'The pending order will be cancelled on Delta Exchange and the trade record closed.',
      confirmLabel: 'Cancel order',
      destructive: true,
    });
    if (!ok) return;
    try {
      await tradingService.cancelDraftTrade(tradeId);
      fetchTrades();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to cancel draft trade');
    }
  };

  const handleResolveLimbo = async () => {
    if (!limboDialogTradeId) return;
    const price = parseFloat(limboClosePrice);
    if (isNaN(price) || price <= 0) {
      setError('Please enter a valid close price');
      return;
    }
    try {
      await tradingService.resolveLimboTrade(limboDialogTradeId, price);
      setLimboDialogTradeId(null);
      setLimboClosePrice('');
      fetchTrades();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to resolve LIMBO trade');
    }
  };

  const handleSaveOrderEdit = async (orderId: number) => {
    const price = parseFloat(editOrderPrice);
    const qty = parseFloat(editOrderQty);
    if (isNaN(price) || price <= 0) return;
    try {
      await api.put(`/orders/${orderId}?force=true`, {
        price,
        ...(qty > 0 ? { quantity: qty, filledQuantity: qty } : {})
      });
      setEditingOrderId(null);
      fetchTrades();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update order');
    }
  };

  const handleOpenTradeDetail = async (trade: Trade) => {
    setDrawerTradeId(trade.id);
    setDrawerLoading(true);
    try {
      const response = await api.get(`/trades/${trade.id}`);
      setDrawerTrade((response.data as any).data || trade);
    } catch {
      setDrawerTrade(trade);
    }
    setDrawerLoading(false);
  };

  const formatCurrency = (value: number, decimals = 2): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  // Price-level formatter (no currency symbol) — for Entry/Current/SL/Target columns
  /* formatPriceLevel used to be defined right here, as a private copy of
     `formatPrice`. It carried the same defect the shared one did — the API sends
     Prisma Decimals as STRINGS and String.prototype.toLocaleString accepts then
     IGNORES its options, so "731.91248571" came back unchanged — and every call
     site passed the RAW stored decimalPrecision (the DELTA import's blanket 8)
     rather than the tick-capped resolver. Two bugs, stacked, in a copy of logic
     that already existed centrally.

     It now delegates: one coercing formatter, one precision resolver, both
     already unit-tested. Signature kept so the ten call sites read the same. */
  const formatPriceLevel = (value: number, decimals = 2): string =>
    formatPrice(value, decimals);

  /* The size a trade actually TRADED.
   *
   * `quantity` is the size still OPEN, not the size dealt: positionSync zeroes it
   * when a position closes and stores the executed size beside it in
   * `closeQuantity` (positionSync.service.ts:883). Reading `quantity` alone is why
   * every closed row printed `Qty 0` and `—` for R next to a real P&L — 68 of the
   * 194 closed rows on page one.
   *
   * Mirrors COALESCE(NULLIF(quantity,0), closeQuantity, 0), the rule
   * migrations/1783800000000-BackfillClosedRealizedPnlLotSize.ts already uses.
   *
   * Returns 0 when the trade carries NEITHER. That is not "no size" — it is size
   * UNKNOWN: five Delta option legs closed via broker_realized(sync) took their P&L
   * straight from the broker ledger and never had closePrice or closeQuantity stamped.
   * The cell renders those as an em-dash, because printing 0 next to Rs 1,249.50 of
   * realised P&L states something the app does not know. */
  const executedQty = (t: any): number => {
    const q = Number(t?.quantity);
    if (Number.isFinite(q) && q > 0) return q;
    const c = Number(t?.closeQuantity);
    return Number.isFinite(c) && c > 0 ? c : 0;
  };

  /* FROZEN COLUMNS.
   *
   * The table is 20 columns and ~2,250px wide. At 1366px it overflowed its
   * container by 986px with no scroll cue, so SL, Target, the timestamps and —
   * critically — ACTIONS were unreachable: you could not act on a row without
   * discovering a hidden horizontal scroll. Freezing the leading identity
   * columns and the trailing action column keeps both anchors put while the
   * middle scrolls.
   *
   * The two-layer background is the trick already used in DashboardLayout: a
   * sticky cell needs an OPAQUE base or the scrolling columns show through it,
   * but the row tint (P&L colour / issue / profit-lock) is translucent. So paint
   * background.paper underneath and the row tint on top of it. */
  const FROZEN_W = [44, 44, 190];   // checkbox / expander / symbol
  const frozenLeftSx = (i: number, head: boolean, rowBg?: string) => ({
    position: 'sticky' as const,
    left: FROZEN_W.slice(0, i).reduce((a, b) => a + b, 0),
    width: FROZEN_W[i], minWidth: FROZEN_W[i], maxWidth: FROZEN_W[i],
    zIndex: head ? 5 : 2,
    backgroundColor: 'background.paper',
    ...(rowBg ? { backgroundImage: 'linear-gradient(' + rowBg + ', ' + rowBg + ')' } : {}),
    ...(i === FROZEN_W.length - 1
      ? { borderRight: '1px solid ' + chHairline(isDark) } : {}),
  });
  const frozenRightSx = (head: boolean, rowBg?: string) => ({
    position: 'sticky' as const,
    right: 0,
    zIndex: head ? 5 : 2,
    backgroundColor: 'background.paper',
    ...(rowBg ? { backgroundImage: 'linear-gradient(' + rowBg + ', ' + rowBg + ')' } : {}),
    borderLeft: '1px solid ' + chHairline(isDark),
  });

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatDateTime = (date: Date | string | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPnLColor = (pnl: number): 'success.main' | 'error.main' | 'text.secondary' => {
    if (pnl > 0) return 'success.main';
    if (pnl < 0) return 'error.main';
    return 'text.secondary';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        background: 'background.paper'
      }}
    >
      {/* Margin risk for the whole account, above the positions it applies to.
          Renders nothing below WARNING — a banner that is always on stops being read. */}
      <LiquidationRiskBanner />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Trades Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedTrades.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
              sx={{ borderRadius: 2 }}
            >
              Delete Selected ({selectedTrades.size})
            </Button>
          )}
          <Tooltip title="Download the trades currently listed below as a CSV file." arrow>
          <span style={{ display: 'inline-flex' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (trades.length === 0) return;
              const headers = ['Symbol', 'Side', 'Status', 'Quantity', 'Entry Price', 'Current Price', 'Stop Loss', 'Target', 'P&L', 'P&L%', 'Created', 'Closed'];
              const escapeField = (val: string) => `"${val.replace(/"/g, '""')}"`;
              const rows = trades.map((trade) => [
                escapeField(trade.symbol),
                trade.side,
                trade.status,
                trade.quantity,
                trade.averageEntryPrice,
                trade.currentPrice,
                trade.stopLoss ?? '',
                trade.target ?? '',
                trade.totalPnl,
                trade.pnlPercent,
                trade.createdAt ? new Date(trade.createdAt).toLocaleString('en-IN') : '',
                trade.closedAt ? new Date(trade.closedAt).toLocaleString('en-IN') : ''
              ].join(','));
              const csvContent = [headers.join(','), ...rows].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `trades_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }}
            disabled={trades.length === 0}
            sx={{ borderRadius: 2 }}
          >
            Export CSV
          </Button>
          </span>
          </Tooltip>
          <Tooltip title="Re-sync open positions from the broker and refresh the table." arrow>
          <span style={{ display: 'inline-flex' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Sync & Refresh
          </Button>
          </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Section 1: Option baskets (collapsible, on top) ───────────── */}
      {!propTrades && (
        <Box sx={{ mb: 2 }}>
          <SectionBar
            open={basketsOpen}
            onToggle={() => setBasketsOpen(o => !o)}
            title="Option baskets"
            chip={<Chip size="small" label="multi-leg · auto-managed" sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700 }} />}
          />
          <Collapse in={basketsOpen} unmountOnExit>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Multi-leg option structures placed from <strong>Strategies → Options AT</strong> or the chart builder.
                Auto-managed (take-profit / stop / pre-expiry roll-off); closed baskets show why they exited and the realised outcome.
              </Typography>
              <OptionBasketsMonitor />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ── Section 2: single-instrument trades (collapsible) ──────────── */}
      {!propTrades && (
        <SectionBar
          open={tradesOpen}
          onToggle={() => setTradesOpen(o => !o)}
          title="Trades"
          chip={<Chip size="small" color="default" label={`${totalRows} total`} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700 }} />}
        />
      )}
      <Collapse in={propTrades ? true : tradesOpen}>
      <Box sx={{ pt: propTrades ? 0 : 2 }}>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3, md: 2.6 }}>
          <TextField
            fullWidth
            label="Search Symbol"
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchTrades()}
            size="small"
            sx={{ borderRadius: 2 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 2.35 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="OPEN">Open</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
              <MenuItem value="PARTIAL">Partial</MenuItem>
              <MenuItem value="LIMBO">Limbo</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 2.35 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Side</InputLabel>
            <Select
              value={sideFilter}
              label="Side"
              onChange={(e) => {
                setSideFilter(e.target.value);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="LONG">Long</MenuItem>
              <MenuItem value="SHORT">Short</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 2.35 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Sort by</InputLabel>
            <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="createdAt">Created (newest)</MenuItem>
              <MenuItem value="closedAt">Closed date (newest)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 2.6 }}>
          <Autocomplete
            size="small"
            options={availableTags}
            value={tagFilter || null}
            onChange={(_, v) => setTagFilter(v || '')}
            renderInput={(params) => <TextField {...params} label="Tag" placeholder="Filter by tag" />}
            isOptionEqualToValue={(o, v) => o === v}
            clearOnEscape
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && trades.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : trades.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No trades found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click "Sync & Refresh" to fetch trades from Delta Exchange India
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{
          borderRadius: 2, border: '1px solid', borderColor: 'divider',
          maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto',
          /* The horizontal scroll was invisible — no bar until hover, and nothing
             said the row continued past the edge. Keep it shown, with enough
             contrast to read as an affordance. */
          '&::-webkit-scrollbar': { height: 10, width: 10 },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 5,
            backgroundColor: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.28)',
          },
          scrollbarWidth: 'thin',
        }}>
          <Table stickyHeader size="small" sx={{
            /* ~80px rows over 100 rendered rows was ~8,000px of table. */
            '& .MuiTableCell-root': { py: 0.5, fontSize: CH_TYPE.small, whiteSpace: 'nowrap' },
          }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ fontWeight: 'bold', ...frozenLeftSx(0, true) }}>
                  <Checkbox
                    checked={selectedTrades.size === trades.length && trades.length > 0}
                    indeterminate={selectedTrades.size > 0 && selectedTrades.size < trades.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={frozenLeftSx(1, true)} />
                <TableCell sx={{ fontWeight: 'bold', ...frozenLeftSx(2, true) }}>Symbol</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Side</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>P&L</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>P&L %</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>R</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Entry</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Exit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Current</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>SL</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>SL Risk (₹)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Target</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Target Gain (₹)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Initiated</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Closed</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>SL Trigger</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', ...frozenRightSx(true) }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.map((trade) => {
                // Operative SL mirrors the BACKEND breach: trailing SL when
                // active, else the user's manual SL (full-manual-control turns
                // trailing OFF and makes the manual SL authoritative — even if
                // it's looser than the initial SL), else the initial SL. The SL
                // Risk + profit-lock badge use this same number, matching the
                // chart line and the Dashboard.
                const _tslV = (trade as any).currentTrailingSl;
                const _manV = (trade as any).manualStopLoss;
                const _effectiveSL =
                  (_tslV != null && Number.isFinite(Number(_tslV))) ? Number(_tslV)
                  : (_manV != null && Number.isFinite(Number(_manV))) ? Number(_manV)
                  : (trade.stopLoss != null && Number.isFinite(Number(trade.stopLoss))) ? Number(trade.stopLoss)
                  : null;
                const _slSource: 'TSL' | 'M' | 'I' | null = (() => {
                  if (_effectiveSL == null) return null;
                  const tsl = (trade as any).currentTrailingSl;
                  const m = (trade as any).manualStopLoss;
                  if (tsl != null && Math.abs(Number(tsl) - _effectiveSL) < 1e-9) return 'TSL';
                  if (m != null && Math.abs(Number(m) - _effectiveSL) < 1e-9) return 'M';
                  return 'I';
                })();
                const _lotSz = (trade as any).lotSize || 1;
                const _usdToInr = ((trade as any).exchange || '').toUpperCase() === 'DELTA' ? USD_TO_INR : 1;
                let _slRiskRs: number | null = null;
                if (_effectiveSL != null && trade.averageEntryPrice && trade.quantity) {
                  _slRiskRs = trade.side === 'LONG'
                    ? (trade.averageEntryPrice - _effectiveSL) * trade.quantity * _lotSz * _usdToInr
                    : (_effectiveSL - trade.averageEntryPrice) * trade.quantity * _lotSz * _usdToInr;
                }
                const _pnl = (trade as any).totalPnl ?? (trade as any).unrealizedPnl ?? 0;
                const _profitLocked = _slRiskRs !== null && _slRiskRs > 0 && _pnl >= profitLockMult * _slRiskRs;

                /* Hoisted so the frozen cells can repaint the same tint. */
                const _rowBg = trade.hasIssue ? C.warnMuted : (_profitLocked ? C.warnMuted : getTradeRowBg(trade, C));
                return (
                <React.Fragment key={trade.id}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: _rowBg,
                      borderLeft: trade.hasIssue ? `3px solid ${C.warn}` : (_profitLocked ? `3px solid ${C.warn}` : undefined),
                      '&:hover': { filter: 'brightness(0.97)' },
                      '& > *': { borderBottom: expandedRows.has(trade.id) ? 0 : undefined }
                    }}
                  >
                    <TableCell padding="checkbox" sx={frozenLeftSx(0, false, _rowBg)}>
                      <Checkbox
                        checked={selectedTrades.has(trade.id)}
                        onChange={() => toggleSelectTrade(trade.id)}
                      />
                    </TableCell>
                    <TableCell sx={frozenLeftSx(1, false, _rowBg)}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(trade.id)}
                      >
                        {expandedRows.has(trade.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={frozenLeftSx(2, false, _rowBg)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip arrow title={trade.symbol + ' \u00b7 ' + trade.tradeId}>
                          <Typography variant="body2" fontWeight="bold" noWrap sx={{ color: C.neutral, cursor: 'help' }}>
                            {trade.symbol}
                          </Typography>
                        </Tooltip>
                        {trade.isSimulation && (
                          <Tooltip title="PAPER trade — placed from the chart as a simulation. No broker order; not in your real P&L, loss cap or reports.">
                            <Chip label="PAPER" size="small" data-testid="trade-paper-chip"
                              sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#546e7a', color: '#fff', '& .MuiChip-label': { px: 0.6 } }} />
                          </Tooltip>
                        )}
                        {_profitLocked && (
                          <Tooltip title={`Profit Lock: P&L ≥ ${profitLockMult}× SL Risk — consider securing profits`}>
                            <span style={{ fontSize: '0.85rem' }}>🔒</span>
                          </Tooltip>
                        )}
                        {trade.hasIssue && (
                          <Tooltip title={trade.issueDescription || 'Trade has an issue'}>
                            <WarningAmberIcon sx={{ fontSize: 16, color: C.warn }} />
                          </Tooltip>
                        )}
                        {/* Position review, on OPEN rows only.
                            This is the one surface where the useful question is "should I still
                            be holding this" rather than "is this setup any good", so the chip is
                            mounted in POSITION mode. Click-to-fetch, never automatic: a table of
                            rows that assessed themselves would spend money simply by being
                            looked at. A closed trade gets no chip -- there is nothing to hold. */}
                        {(trade.status === 'OPEN' || trade.status === 'PARTIAL') && (
                          <AiVerdictChip
                            mode="POSITION"
                            symbol={trade.symbol}
                            exchange={((trade as any).instrument?.exchange
                              || (trade as any).exchange || '').toUpperCase()}
                            signalDirection={trade.side === 'SHORT' ? 'SHORT' : 'LONG'}
                            askLabel="Review"
                            askSource="trades table"
                          />
                        )}
                      </Box>
                      {/* The internal id (TRD_SYNC_1786979026916_32nqkp1f9) used to take a
                          full line under every symbol. It is a support handle, not something
                          you read while scanning — it is in the symbol's tooltip now. */}
                      {/* Phase B — tag chips */}
                      {(((trade as any).tags as string[]) || []).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.25 }}>
                          {((trade as any).tags as string[]).slice(0, 4).map((t) => (
                            <Chip
                              key={t}
                              label={t}
                              size="small"
                              sx={{ height: 16, fontSize: '0.6rem', bgcolor: C.accentMuted, color: C.accent, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }}
                            />
                          ))}
                          {((trade as any).tags as string[]).length > 4 && (
                            <Chip
                              label={`+${((trade as any).tags as string[]).length - 4}`}
                              size="small"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={trade.side}
                        color={trade.side === 'LONG' ? 'success' : 'error'}
                        size="small"
                        icon={trade.side === 'LONG' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip status={trade.status} C={C} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={trade.status === 'DRAFT' ? 'text.secondary' : getPnLColor(trade.totalPnl)}
                      >
                        {trade.status === 'DRAFT' ? '-' : formatCurrency(trade.totalPnl)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={trade.status === 'DRAFT' ? 'text.secondary' : getPnLColor(trade.pnlPercent)}
                      >
                        {trade.status === 'DRAFT' ? 'Pending' : formatPercentage(trade.pnlPercent)}
                      </Typography>
                    </TableCell>
                    {/* Phase B — Realised R-multiple (Σ realised P&L ÷ initial SL risk).
                        Initial risk uses the stable `stopLoss` (not `manualStopLoss`)
                        so the R baseline doesn't drift when the user edits the SL. */}
                    <TableCell align="right">
                      {(() => {
                        if (trade.status === TradeStatus.DRAFT) {
                          return <Typography variant="body2" color="text.secondary">—</Typography>;
                        }
                        const entry = trade.averageEntryPrice ? Number(trade.averageEntryPrice) : null;
                        const initialSL = trade.stopLoss != null ? Number(trade.stopLoss) : null;
                        const qty = executedQty(trade);
                        const lotSz = (trade as any).lotSize || 1;
                        const usdToInr = ((trade as any).exchange || '').toUpperCase() === 'DELTA' ? USD_TO_INR : 1;
                        const realized = Number((trade as any).realizedPnl || 0);
                        const live = Number((trade as any).totalPnl ?? (trade as any).unrealizedPnl ?? 0);
                        const pnl = trade.status === TradeStatus.CLOSED ? realized : live;
                        if (!entry || !initialSL || !qty) {
                          return <Typography variant="body2" color="text.secondary">—</Typography>;
                        }
                        const initialRisk = Math.abs(entry - initialSL) * qty * lotSz * usdToInr;
                        if (initialRisk <= 0) {
                          return <Typography variant="body2" color="text.secondary">—</Typography>;
                        }
                        const r = pnl / initialRisk;
                        const color = r > 0 ? C.bull : r < 0 ? C.bear : C.dim;
                        return (
                          <Tooltip arrow title={`Initial risk ₹${initialRisk.toFixed(0)} · P&L ₹${pnl.toFixed(0)}`}>
                            <Typography variant="body2" fontWeight="bold" sx={{ color, fontVariantNumeric: 'tabular-nums' }}>
                              {r >= 0 ? '+' : ''}{r.toFixed(2)}R
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {executedQty(trade) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatPriceLevel(trade.averageEntryPrice, resolveDecimalsFor((trade as any).instrument, trade.averageEntryPrice))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={trade.closePrice ? 'text.primary' : 'text.disabled'}>
                        {trade.closePrice ? formatPriceLevel(trade.closePrice, resolveDecimalsFor((trade as any).instrument, trade.closePrice)) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatPriceLevel(trade.currentPrice, resolveDecimalsFor((trade as any).instrument, trade.currentPrice))}
                      </Typography>
                    </TableCell>
                    {/* SL cell */}
                    <TableCell align="right">
                      {editingSLTradeId === trade.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                          <TextField
                            size="small"
                            value={editingSLValue}
                            onChange={e => setEditingSLValue(e.target.value)}
                            sx={{ width: 90 }}
                            inputProps={{ style: { padding: '4px 8px', fontSize: '0.75rem' } }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveSL(trade.id);
                              if (e.key === 'Escape') setEditingSLTradeId(null);
                            }}
                          />
                          <IconButton size="small" onClick={() => handleSaveSL(trade.id)} sx={{ color: C.bull }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setEditingSLTradeId(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: trade.status === 'OPEN' ? 'pointer' : 'default', justifyContent: 'flex-end' }}
                          onClick={() => {
                            if (trade.status === TradeStatus.OPEN) {
                              setEditingSLTradeId(trade.id);
                              setEditingSLValue((trade.manualStopLoss || trade.stopLoss)?.toString() || '');
                            }
                          }}
                        >
                          {_effectiveSL != null && _slSource ? (() => {
                            // F44 — when the trade row was created from an
                            // alert AND the operative source is the Initial
                            // value (i.e. user hasn't overridden), this is
                            // an Auto-derived SL. Show it as the "AUTO"
                            // chip variant so users can tell at a glance
                            // that the level came from R115 Auto-ISL
                            // (suggestedIsl on the alert) rather than a
                            // hand-entered value.
                            const isAutoFromAlert = _slSource === 'I' && (trade as any).alertId != null;
                            const chipLabel = isAutoFromAlert ? 'AUTO' : _slSource;
                            return (
                            <Tooltip
                              title={(() => {
                                const lines: string[] = [];
                                const sourceLabel = isAutoFromAlert
                                  ? 'Auto from alert — R115 suggestedIsl seeded at signal time (min/max of prev 2 CLH ± Breach Buffer)'
                                  : _slSource === 'TSL' ? 'Trailing — live breach level'
                                  : _slSource === 'M'   ? 'Manual override'
                                  :                       'Initial from alert';
                                lines.push(`Operative SL: ${formatPriceLevel(_effectiveSL, resolveDecimalsFor((trade as any).instrument, _effectiveSL))} (${sourceLabel})`);
                                if ((trade as any).currentTrailingSl != null && Math.abs(Number((trade as any).currentTrailingSl) - _effectiveSL) > 1e-9) {
                                  lines.push(`TSL: ${formatPriceLevel(Number((trade as any).currentTrailingSl), resolveDecimalsFor((trade as any).instrument, Number((trade as any).currentTrailingSl)))}`);
                                }
                                if ((trade as any).manualStopLoss != null && Math.abs(Number((trade as any).manualStopLoss) - _effectiveSL) > 1e-9) {
                                  lines.push(`Manual: ${formatPriceLevel(Number((trade as any).manualStopLoss), resolveDecimalsFor((trade as any).instrument, Number((trade as any).manualStopLoss)))}`);
                                }
                                if (trade.stopLoss != null && Math.abs(Number(trade.stopLoss) - _effectiveSL) > 1e-9) {
                                  lines.push(`Initial (R-anchor): ${formatPriceLevel(Number(trade.stopLoss), resolveDecimalsFor((trade as any).instrument, Number(trade.stopLoss)))}`);
                                }
                                return lines.join(' · ');
                              })()}
                              arrow
                            >
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                <Chip label={chipLabel} size="small"
                                  sx={{ height: 14, fontSize: '0.55rem', fontWeight: 700,
                                    // Transparent for the same reason as StatusChip: the row
                                    // beneath is already tinted, and a second fill took this
                                    // to 3.67:1. The border carries the colour.
                                    bgcolor: 'transparent',
                                    color:   _slSource === 'TSL' ? C.bull      : _slSource === 'M' ? C.warn      : isAutoFromAlert ? C.neutral : C.bear,
                                    border: `1px solid ${_slSource === 'TSL' ? C.bullBorder : _slSource === 'M' ? C.warnBorder : isAutoFromAlert ? C.neutralBorder : C.bearBorder}` }} />
                                <Typography variant="body2" fontWeight="bold"
                                  sx={{ color: _slSource === 'TSL' ? C.bull : _slSource === 'M' ? C.warn : C.bear }}>
                                  {formatPriceLevel(_effectiveSL, resolveDecimalsFor((trade as any).instrument, _effectiveSL))}
                                </Typography>
                              </Box>
                            </Tooltip>
                            );
                          })() : trade.status === TradeStatus.OPEN ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                              <Typography variant="caption" sx={{ color: C.dim }}>Set SL</Typography>
                              {/* F43 — deep-link to Charts with Add ISL pre-armed. */}
                              <Tooltip arrow title="Open chart and pick the SL on price">
                                <IconButton size="small" sx={{ p: 0.2 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const sym = (trade as any).instrument?.symbol;
                                    const exch = (trade as any).instrument?.exchange || '';
                                    if (sym) window.open(`/charts?symbol=${encodeURIComponent(sym)}&exchange=${encodeURIComponent(exch)}&action=addIsl`, '_blank', 'noopener');
                                  }}>
                                  <OpenInNewIcon sx={{ fontSize: 12, color: C.dim }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                          {trade.status === TradeStatus.OPEN && <EditIcon sx={{ fontSize: 12, color: C.dim }} />}
                        </Box>
                      )}
                    </TableCell>
                    {/* SL Risk (₹) cell — uses _slRiskRs computed from the
                        operative SL (TSL/M/I) for consistency with the cell
                        above. Negative = profit-locked (TSL above entry for
                        LONG). */}
                    <TableCell align="right">
                      {(() => {
                        if (_slRiskRs == null) {
                          return <Typography variant="body2" color="text.secondary">—</Typography>;
                        }
                        const slRisk = _slRiskRs;
                        return (
                          <Typography variant="body2" fontWeight="bold" sx={{ color: slRisk <= 0 ? C.bull : C.bear }}>
                            {slRisk <= 0 ? `+${Math.abs(Math.round(slRisk)).toLocaleString('en-IN')}🔒` : Math.round(slRisk).toLocaleString('en-IN')}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    {/* Target cell with inline editing */}
                    <TableCell align="right">
                      {editingTargetTradeId === trade.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                          <TextField
                            size="small"
                            value={editingTargetValue}
                            onChange={e => setEditingTargetValue(e.target.value)}
                            sx={{ width: 90 }}
                            inputProps={{ style: { padding: '4px 8px', fontSize: '0.75rem' } }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveTarget(trade.id);
                              if (e.key === 'Escape') setEditingTargetTradeId(null);
                            }}
                          />
                          <IconButton size="small" onClick={() => handleSaveTarget(trade.id)} sx={{ color: C.bull }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setEditingTargetTradeId(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: trade.status === 'OPEN' ? 'pointer' : 'default', justifyContent: 'flex-end' }}
                          onClick={() => {
                            if (trade.status === TradeStatus.OPEN) {
                              setEditingTargetTradeId(trade.id);
                              setEditingTargetValue(trade.target?.toString() || '');
                            }
                          }}
                        >
                          {trade.target ? formatPriceLevel(Number(trade.target), resolveDecimalsFor((trade as any).instrument, Number(trade.target))) : (
                            trade.status === TradeStatus.OPEN ? (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                <Typography variant="caption" sx={{ color: C.dim }}>Set target</Typography>
                                {/* F43 — deep-link to Charts with Add Tgt pre-armed. */}
                                <Tooltip arrow title="Open chart and pick the Target on price">
                                  <IconButton size="small" sx={{ p: 0.2 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const sym = (trade as any).instrument?.symbol;
                                      const exch = (trade as any).instrument?.exchange || '';
                                      if (sym) window.open(`/charts?symbol=${encodeURIComponent(sym)}&exchange=${encodeURIComponent(exch)}&action=addTgt`, '_blank', 'noopener');
                                    }}>
                                    <OpenInNewIcon sx={{ fontSize: 12, color: C.dim }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : '-'
                          )}
                          {trade.status === TradeStatus.OPEN && <EditIcon sx={{ fontSize: 12, color: C.dim }} />}
                        </Box>
                      )}
                    </TableCell>
                    {/* Issue 7: Target Gain cell */}
                    <TableCell align="right">
                      {(() => {
                        const target = trade.target;
                        if (!target || !trade.averageEntryPrice || !trade.quantity) {
                          return <Typography variant="body2" color="text.secondary">—</Typography>;
                        }
                        const lotSz = (trade as any).lotSize || 1;
                        const usdToInr = ((trade as any).exchange || '').toUpperCase() === 'DELTA' ? USD_TO_INR : 1;
                        const gain = trade.side === 'LONG'
                          ? (target - trade.averageEntryPrice) * trade.quantity * lotSz * usdToInr
                          : (trade.averageEntryPrice - target) * trade.quantity * lotSz * usdToInr;
                        return (
                          <Typography variant="body2" fontWeight="bold" sx={{ color: gain >= 0 ? C.bull : C.bear }}>
                            {Math.round(gain).toLocaleString('en-IN')}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(trade.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(trade.closedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={trade.slTriggerEnabled !== false ? 'SL auto-close ON — disable to alert-only' : 'SL alert-only (no auto-close)'}>
                        <Switch
                          size="small"
                          checked={trade.slTriggerEnabled !== false}
                          disabled={trade.status !== TradeStatus.OPEN}
                          onChange={async (e) => {
                            try {
                              await api.put(`/trades/${trade.id}/sl-trigger`, { slTriggerEnabled: e.target.checked });
                            } catch {
                              // error is non-critical; table will refresh on next poll
                            }
                          }}
                          color="error"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={frozenRightSx(false, _rowBg)}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleOpenTradeDetail(trade)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit tags">
                          <IconButton size="small" onClick={() => openTagDialog(trade)}>
                            <LocalOfferOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={(trade as any).notes ? 'Edit note' : 'Add note'}>
                          <IconButton size="small" onClick={() => openNotesDialog(trade)}>
                            <NoteAltOutlinedIcon fontSize="small" color={(trade as any).notes ? 'primary' : 'inherit'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Replay on chart (preset = cloud)">
                          <span>
                            <IconButton size="small" onClick={() => handleReplay(trade)} disabled={!trade.symbol}>
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {(trade.status === TradeStatus.OPEN || trade.status === TradeStatus.PARTIAL) && (
                          <>
                            <Tooltip title="Add to Position">
                              <IconButton size="small" color="primary">
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Close Trade">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCloseTrade(trade.id)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {trade.status === TradeStatus.DRAFT && (
                          <>
                            <Chip
                              label="Pending"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                            <Tooltip title="Cancel Draft Order">
                              <IconButton
                                size="small"
                                sx={{ color: C.warn }}
                                onClick={() => handleCancelDraft(trade.id)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {(trade.status as any) === 'LIMBO' && (
                          <Tooltip title="Resolve LIMBO Trade">
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ color: C.accent, borderColor: C.accentBorder, fontSize: '0.65rem', px: 0.8, py: 0.2, minWidth: 0 }}
                              onClick={() => {
                                setLimboDialogTradeId(trade.id);
                                setLimboClosePrice(trade.currentPrice?.toString() || '');
                              }}
                            >
                              Resolve
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={15}>
                      <Collapse in={expandedRows.has(trade.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                          <Typography variant="h6" gutterBottom component="div" sx={{ color: C.neutral, fontWeight: 'bold' }}>
                            Orders History
                          </Typography>
                          {trade.orders && trade.orders.length > 0 ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Side</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Filled</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Price</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Commission</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Executed At</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Edit</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {trade.orders.map((order: any) => (
                                  <TableRow key={order.id}>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {order.orderId}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={order.orderType} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={order.side}
                                        color={order.side === 'BUY' ? 'success' : 'error'}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={order.status} size="small" color="info" />
                                    </TableCell>
                                    <TableCell align="right">{order.quantity}</TableCell>
                                    <TableCell align="right">{order.filledQuantity}</TableCell>
                                    <TableCell align="right">
                                      {editingOrderId === order.id ? (
                                        <TextField size="small" value={editOrderPrice} onChange={e => setEditOrderPrice(e.target.value)}
                                          sx={{ width: 90 }} inputProps={{ style: { fontSize: '0.75rem', padding: '2px 6px' } }} />
                                      ) : (
                                        <Box sx={{ color: !order.price ? C.bear : 'inherit', fontWeight: !order.price ? 700 : 'normal' }}>
                                          {order.price ? formatPriceLevel(order.price, resolveDecimalsFor((trade as any).instrument, order.price)) : '⚠ missing'}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(order.commission || 0)}</TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {formatDateTime(order.createdAt)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {formatDateTime(order.executedAt)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {editingOrderId === order.id ? (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <IconButton size="small" sx={{ color: C.bull }} onClick={() => handleSaveOrderEdit(order.id)}>
                                            <CheckIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton size="small" sx={{ color: C.dim }} onClick={() => setEditingOrderId(null)}>
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ) : (
                                        <Tooltip title="Force-edit price (fixes missing MARKET order price)">
                                          <IconButton size="small" sx={{ color: C.dim }}
                                            onClick={() => { setEditingOrderId(order.id); setEditOrderPrice(order.price ? String(order.price) : ''); setEditOrderQty(String(order.filledQuantity || order.quantity || '')); }}>
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              No orders found for this trade. Orders will be created during position sync.
                            </Alert>
                          )}

                          {/* Round 28 — trade-event timeline. Lazy-loaded
                             when the user expands the row; cached in
                             tradeEvents state. Helps reconstruct what
                             happened (SL modified / TSL activated /
                             breach detected / close fired) so the user
                             can audit unexpected closes. */}
                          <TradeEventsTimeline tradeId={trade.id} expanded={expandedRows.has(trade.id)} C={C} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* R69b — server-side pagination control. Only renders when there's
          something to page through (totalRows > pageSize means there's at
          least one extra page). */}
      {!propTrades && totalRows > pageSize && (
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const n = parseInt(e.target.value, 10);
            setPageSize(n);
            setPage(0);
            try { localStorage.setItem('trades_page_size', String(n)); } catch { /* ignore */ }
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}
        />
      )}

      {trades.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {trades.length} of {totalRows} trade{totalRows !== 1 ? 's' : ''}
            {selectedTrades.size > 0 && ` • ${selectedTrades.size} selected`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip
              label={`Profitable: ${trades.filter(t => t.totalPnl > 0).length}`}
              color="success"
              size="small"
            />
            <Chip
              label={`Loss: ${trades.filter(t => t.totalPnl < 0).length}`}
              color="error"
              size="small"
            />
            <Chip
              label={`Draft: ${trades.filter(t => t.status === 'DRAFT').length}`}
              color="info"
              size="small"
            />
          </Box>
        </Box>
      )}

      </Box>
      </Collapse>

      {/* Phase B — Tag Edit Dialog */}
      <Dialog open={tagDialogTrade !== null} onClose={() => setTagDialogTrade(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit tags · {tagDialogTrade?.symbol}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Comma-separated tags (max 12, ≤32 chars each). Examples: <em>FOMC, breakout, fakeout, earnings</em>.
            Tag breakdown shows up on the Analytics page.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Tags"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            size="small"
            placeholder="FOMC, breakout, fakeout"
            onKeyDown={(e) => { if (e.key === 'Enter') saveTagsForDialog(); }}
          />
          {tagDraft.trim() && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
              {Array.from(new Set(tagDraft.split(',').map(s => s.trim()).filter(Boolean))).slice(0, 12).map(t => (
                <Chip key={t} label={t} size="small" sx={{ bgcolor: C.accentMuted, color: C.accent, fontWeight: 700 }} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogTrade(null)}>Cancel</Button>
          <Button onClick={saveTagsForDialog} variant="contained" disabled={tagSubmitting}>
            {tagSubmitting ? 'Saving…' : 'Save tags'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Journal note dialog */}
      <Dialog open={notesDialogTrade !== null} onClose={() => setNotesDialogTrade(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Trade note · {notesDialogTrade?.symbol}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your journal note for this trade — the setup, why you took it, what you'd do differently.
            Pairs with tags for review on the Analytics page.
          </Typography>
          <TextField
            autoFocus fullWidth multiline minRows={4} maxRows={12}
            label="Note" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
            size="small" placeholder="e.g. Entered on the HTF pullback; sized 0.5R; exited early on the wick — should have held to target."
            inputProps={{ maxLength: 2000 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>{notesDraft.length}/2000</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogTrade(null)}>Cancel</Button>
          <Button onClick={saveNotes} variant="contained" disabled={notesSubmitting}>
            {notesSubmitting ? 'Saving…' : 'Save note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* LIMBO Resolve Dialog */}
      <Dialog open={limboDialogTradeId !== null} onClose={() => setLimboDialogTradeId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve LIMBO Trade</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the price at which this trade was closed externally. P&amp;L will be recalculated.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Close Price"
            type="number"
            value={limboClosePrice}
            onChange={(e) => setLimboClosePrice(e.target.value)}
            size="small"
            inputProps={{ step: 'any', min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimboDialogTradeId(null)}>Cancel</Button>
          <Button onClick={handleResolveLimbo} variant="contained" sx={{ bgcolor: C.accent }}>
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trade Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerTradeId !== null}
        onClose={() => { setDrawerTradeId(null); setDrawerTrade(null); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}
      >
        {drawerLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : drawerTrade ? (
          <Box>
            {/* Header */}
            <Box sx={{
              p: 2.5,
              background: drawerTrade.side === 'LONG'
                ? `linear-gradient(135deg, ${C.bull} 0%, ${C.bullBorder} 100%)`
                : `linear-gradient(135deg, ${C.bear} 0%, ${C.bearBorder} 100%)`,
              color: '#fff'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {drawerTrade.instrument?.symbol || drawerTrade.symbol || 'Unknown'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={drawerTrade.side} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                    <Chip label={drawerTrade.status} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                  </Box>
                </Box>
                <IconButton onClick={() => { setDrawerTradeId(null); setDrawerTrade(null); }} sx={{ color: '#fff' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Entry</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{Number(drawerTrade.averageEntryPrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>P&L</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: Number(drawerTrade.realizedPnl || drawerTrade.totalPnl || 0) >= 0 ? '#86efac' : '#fca5a5', opacity: 0.95 }}>
                    {Number(drawerTrade.realizedPnl || drawerTrade.totalPnl || 0) >= 0 ? '+' : ''}₹{Number(drawerTrade.realizedPnl || drawerTrade.totalPnl || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Qty</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{executedQty(drawerTrade) || '—'}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ p: 2.5, overflowY: 'auto', height: 'calc(100vh - 200px)' }}>
              {/* Trade details */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Trade Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3 }}>
                {[
                  { label: 'Opened', value: drawerTrade.createdAt ? new Date(drawerTrade.createdAt).toLocaleString('en-IN') : '-' },
                  { label: 'Closed', value: drawerTrade.closedAt ? new Date(drawerTrade.closedAt).toLocaleString('en-IN') : 'Still Open' },
                  { label: 'Stop Loss', value: drawerTrade.manualStopLoss ? `₹${Number(drawerTrade.manualStopLoss).toLocaleString('en-IN')} (M)` : drawerTrade.stopLoss ? `₹${Number(drawerTrade.stopLoss).toLocaleString('en-IN')}` : '-' },
                  { label: 'Target', value: drawerTrade.target ? `₹${Number(drawerTrade.target).toLocaleString('en-IN')}` : '-' },
                  { label: 'P&L %', value: `${drawerTrade.pnlPercent ? Number(drawerTrade.pnlPercent).toFixed(2) : '0.00'}%` },
                ].map(item => (
                  <Box key={item.label} sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: C.dim }}>{item.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Orders Timeline */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Orders
              </Typography>
              {drawerTrade.orders && drawerTrade.orders.length > 0 ? (
                drawerTrade.orders.map((order: any, idx: number) => (
                  <Box key={order.id || idx} sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, mb: 1,
                    borderLeft: `4px solid ${(order.side || order.orderSide) === 'BUY' ? C.bull : C.bear}`
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={order.side || order.orderSide} size="small"
                          sx={{
                            bgcolor: (order.side || order.orderSide) === 'BUY' ? C.bullMuted : C.bearMuted,
                            color: (order.side || order.orderSide) === 'BUY' ? C.bull : C.bear,
                            fontSize: '0.65rem'
                          }} />
                        <Chip label={order.status || order.orderStatus} size="small"
                          sx={{
                            bgcolor: (order.status || order.orderStatus) === 'FILLED' ? C.bullMuted : 'action.hover',
                            color: (order.status || order.orderStatus) === 'FILLED' ? C.bull : 'text.primary',
                            fontSize: '0.65rem'
                          }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: C.dim }}>
                        {order.createdAt ? new Date(order.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: C.dim }}>Qty</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.quantity}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: C.dim }}>Price</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ₹{Number(order.price || order.averagePrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      {order.exchangeOrderId && (
                        <Box>
                          <Typography variant="caption" sx={{ color: C.dim }}>Exchange ID</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>{order.exchangeOrderId}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, color: C.dim }}>
                  <Typography variant="body2">
                    {drawerTrade.status === 'CLOSED' ? 'No closing order found. Run Sync & Refresh.' : 'No orders yet.'}
                  </Typography>
                </Box>
              )}

              {/* LIMBO notice */}
              {drawerTrade.status === 'LIMBO' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <AlertTitle>Closing leg unknown</AlertTitle>
                  This trade was closed externally but no matching closing order was found on the exchange. P&L is set to 0. Use Sync & Refresh to try again or resolve manually.
                </Alert>
              )}

              {/* Notes */}
              {drawerTrade.notes && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: C.warnMuted, borderRadius: 1, border: `1px solid ${C.warnBorder}` }}>
                  <Typography variant="caption" sx={{ color: C.warn, fontWeight: 600 }}>Notes</Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>{drawerTrade.notes}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </Paper>
  );
};

/**
 * TradeEventsTimeline — round 28. Renders the audit log for one trade
 * in chronological order. Each row shows: time, event type chip, LTP,
 * operative SL, target, and the human reason (e.g. "Trail SL ratched
 * 1.045 → 1.038 (normal)"). Helps the user reconstruct the timeline
 * between entry and exit when an unexpected close happens.
 */
interface TradeEventRow {
  id: number;
  tradeId: number;
  eventType: string;
  eventAt: string;
  ltp: number | null;
  sl: number | null;
  target: number | null;
  reason: string | null;
  metadata: any | null;
}
const TradeEventsTimeline: React.FC<{ tradeId: number; expanded: boolean; C: AppColorTokens }> = ({ tradeId, expanded, C }) => {
  const [events, setEvents] = useState<TradeEventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!expanded || loaded) return;
    setLoading(true);
    api.get<any>(`/trades/${tradeId}/events`, { headers: { _suppressToast: 'true' } })
      .then(r => setEvents((r.data?.data || []) as TradeEventRow[]))
      .catch(() => setEvents([]))
      .finally(() => { setLoading(false); setLoaded(true); });
  }, [expanded, loaded, tradeId]);
  if (!expanded) return null;
  const eventColor = (t: string) => {
    if (t === 'CLOSE_FIRED' || t === 'CLOSE_FILLED' || t === 'SL_BREACH_CONFIRMED' || t === 'CANCELLED' || t === 'LIMBO') return C.bear;
    if (t === 'TARGET_HIT_DETECTED') return C.bull;
    if (t === 'TSL_ACTIVATED' || t === 'SPIKE_MODE_ACTIVATED' || t === 'TSL_UPDATED') return C.accent;
    if (t === 'CREATED' || t === 'ENTRY_FILLED') return C.bull;
    if (t === 'SL_MODIFIED' || t === 'TARGET_MODIFIED') return C.neutral;
    if (t === 'SL_BREACH_DETECTED') return C.bear;
    return C.dim;
  };
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom component="div" sx={{ color: C.neutral, fontWeight: 'bold' }}>
        Trade Timeline {events.length > 0 ? `(${events.length})` : ''}
      </Typography>
      {loading ? (
        <Typography variant="body2" sx={{ color: C.dim, fontStyle: 'italic' }}>Loading…</Typography>
      ) : events.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No timeline events for this trade yet. Events are recorded for trades created or modified after the round-28 deploy.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Time (IST)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 200 }}>Event</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>LTP</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>SL</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Target</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(ev => (
              <TableRow key={ev.id}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  {new Date(ev.eventAt).toLocaleString('en-IN', { hour12: false })}
                </TableCell>
                <TableCell>
                  <Chip
                    label={ev.eventType}
                    size="small"
                    sx={{
                      bgcolor: eventColor(ev.eventType) + '22',
                      color: eventColor(ev.eventType),
                      border: `1px solid ${eventColor(ev.eventType)}55`,
                      fontWeight: 700, fontSize: '0.65rem',
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{ev.ltp ?? '—'}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: C.bear }}>{ev.sl ?? '—'}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: C.bull }}>{ev.target ?? '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{ev.reason || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default TradesTable;
